import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface Message {
  id: string;
  content: string;
  type: string;
  timestamp: string;
  agentRead: boolean | null;
  // Read receipt for the SENDER's own bubble: false = sent (✓), true = read (✓✓).
  read?: boolean;
  // Which team member sent this (agent messages only) so multiple agents are
  // distinguishable in the thread.
  agentName?: string;
  agentAvatar?: string;
  // Image/audio attachment, if any.
  attachment?: { url: string; type: 'image' | 'audio'; meta?: any } | null;
}

interface Customer {
  customer_id: string;
  name: string;
  email: string;
  messages: Message[];
  // Add more fields as necessary
  browser?: string;
  ip?: string;
  os?: string;
  page?: string;
  country?: string;
  region?: string;
  org?: string;
  device?: string;
  refer?: string;
  title?: string;
  city?: string;
  history?: any;
  country_name?: string;
  active?: boolean;
  browser_count?: number;
  chat_started?: boolean;
  customer_typing_text: boolean;
  customer_typed_text: string;
  latest_active: Date;
  /** When this visitor's current session started (for time-on-site). */
  first_seen?: string;
  /** Approximate location from IP (for the map). */
  lat?: number | null;
  lng?: number | null;
  /** Blocked visitors can't see the widget anymore. */
  blocked?: boolean;
}

interface TrafficState {
  customers: Customer[];
  initialDataLoaded: string[]; // To keep track of server_ids
}

const initialState: TrafficState = {
  customers: [],
  initialDataLoaded: []
};

const trafficSlice = createSlice({
  name: 'traffic',
  initialState,
  reducers: {
    upsertCustomer: (state, action: PayloadAction<Customer>) => {
      const index = state.customers.findIndex(
        c => c.customer_id === action.payload.customer_id
      );
      if (index >= 0) {
        // Update existing customer
        state.customers[index] = action.payload;
      } else {
        // Add new customer
        state.customers.push(action.payload);
      }
    },

    startChat: (
      state,
      action: PayloadAction<{
        customerId: string;
        message: string;
        token: string;
        ownerToken: string;
        messageId: string;
        avatar_url: string;
        agent_name?: string;
      }>
    ) => {
      const { customerId, message, messageId, avatar_url, agent_name } = action.payload;

      state.customers = state.customers.map(item => {
        if (item.customer_id === customerId) {
          const newMessage: Message = {
            id: messageId, // Ensure that you generate a unique ID or use your preferred ID method
            content: message,
            type: 'agent',
            agentRead: true,
            read: false,
            timestamp: new Date().toISOString(),
            agentName: agent_name,
            agentAvatar: avatar_url
          };

          return {
            ...item,
            chat_started: true,
            typing_text: '',
            active: true,
            messages: [...(item.messages || []), newMessage]
          };
        }
        return item;
      });
    },

    endChat: (
      state,
      action: PayloadAction<{
        messageId: string;
        customerId: string;
        message: string;
        ownerToken: string;
        license_uuid: string;
      }>
    ) => {
      const { customerId, message, messageId, ownerToken, license_uuid } = action.payload;

      state.customers = state.customers.map(item => {
        if (item.customer_id === customerId) {
          const newMessage: Message = {
            id: messageId, // Ensure that you generate a unique ID or use your preferred ID method
            content: message,
            type: 'agent',
            agentRead: true,
            timestamp: new Date().toISOString() // Ensure this matches the type defined in your `Message` interface
          };

          return {
            ...item,
            chat_started: false,
            typing_text: '',
            messages: [...(item.messages || []), newMessage]
          };
        }
        return item;
      });
    },


    endChatbyAgent: (
      state,
      action: PayloadAction<{
        messageId: string;
        customerId: string;
        message: string;
        ownerToken: string;
      }>
    ) => {
      const { customerId, message, messageId, ownerToken } = action.payload;



      state.customers = state.customers.map(item => {
        if (item.customer_id === customerId) 
        {


          if(item.chat_started == true)
          {
            
            const newMessage: Message = {
              id: messageId, // Ensure that you generate a unique ID or use your preferred ID method
              content: message,
              type: 'agent',
              agentRead: true,
              timestamp: new Date().toISOString() // Ensure this matches the type defined in your `Message` interface
            };
  
            return {
              ...item,
              chat_started: false,
              typing_text: '',
              messages: [...(item.messages || []), newMessage]
            };

          }
          else return item;

          
        }
        return item;
      });
    },

    sendMessage: (
      state,
      action: PayloadAction<{
        messageId: string;
        customerId: string;
        message: string;
        ownerToken: string;
        license_uuid: string;
        avatar_url: string;
        agent_name?: string;
        attachment?: any;
      }>
    ) => {
      const { customerId, message, messageId, avatar_url, agent_name, attachment } =
        action.payload;

      state.customers = state.customers.map(item => {
        if (item.customer_id === customerId) {
          const newMessage: Message = {
            id: messageId, // Ensure that you generate a unique ID or use your preferred ID method
            content: message,
            type: 'agent',
            agentRead: true,
            read: false,
            timestamp: new Date().toISOString(),
            agentName: agent_name,
            agentAvatar: avatar_url,
            attachment: attachment || null
          };

          return {
            ...item,
            chat_started: true,
            typing_text: '',
            active: true,
            messages: [...(item.messages || []), newMessage]
          };
        }
        return item;
      });
    },

    // Add a message to a specific customer
    addMessageToCustomer: (
      state,
      action: PayloadAction<{ customer_id: string; message: Message }>
    ) => {
      const customer = state.customers.find(
        c => c.customer_id === action.payload.customer_id
      );
      if (customer) {
        customer.messages.push(action.payload.message);
      }
    },


    addReceivedMessageFromAnotherLocation: (
      state,
      action: PayloadAction<{ customer_id: string; message: Message }>
    ) => {


      const customer = state.customers.find(
        c => c.customer_id === action.payload.customer_id
      );
    
      if (customer) {
        const messageExists = customer.messages.some(
          m => m.id === action.payload.message.id
        );
    
        if (!messageExists) {
          customer.chat_started = true
          customer.messages.push(action.payload.message);
        
        }
      }
    },


    // Bulk update customers
    bulkUpdateCustomers: (
      state,
      action: PayloadAction<{ customers: Customer[]; server_id: string }>
    ) => {
      const { customers, server_id } = action.payload;

      // Check if data from this server_id has already been processed
      if (state.initialDataLoaded.includes(server_id)) {
        return state;
      }

      // Map the incoming customer data to match your application schema
      const updatedCustomers = customers.map((item: any) => {
        const receivedPage = item.page ?? 'n/a';

        return {
          browser: item.browser,
          ip: item.ip,
          os: item.os,
          page: receivedPage,
          country: item.country,
          name: item.name,
          email: item.email,
          customer_id: item.customer_id,
          region: item.region,
          org: item.org,
          messages: [],
          refer: item.refer,
          title: item.title,
          city: item.city,
          history: item.history,
          country_name: item.country_name,
          active: true,
          browser_count: 1,
          chat_started: item.chat_started,
          customer_typing_text: false,
          customer_typed_text: '',
          latest_active: new Date(),
          first_seen: new Date().toISOString(),
          lat: item.lat,
          lng: item.lng
        };
      });

      // Merge by customer_id so a customer already in the store (e.g. added by
      // setCustomerHistory when the agent opened the conversation, or by an
      // add_customer event) is updated rather than duplicated in the list.
      const byId = new Map(state.customers.map(c => [c.customer_id, c]));
      updatedCustomers.forEach(item => {
        const existing = byId.get(item.customer_id);
        if (existing) {
          byId.set(item.customer_id, {
            ...existing,
            ...item,
            // keep any messages we already loaded (history) instead of clearing
            messages:
              existing.messages && existing.messages.length
                ? existing.messages
                : item.messages,
            history: item.history != null ? item.history : existing.history,
            first_seen: existing.first_seen || item.first_seen,
            lat: item.lat != null ? item.lat : existing.lat,
            lng: item.lng != null ? item.lng : existing.lng
          });
        } else {
          byId.set(item.customer_id, item);
        }
      });

      return {
        ...state,
        customers: Array.from(byId.values()),
        initialDataLoaded: [...state.initialDataLoaded, server_id]
      };
    },

    updateCustomer: (state, action: PayloadAction<Customer>) => {
      const payload = action.payload;
      const customer_idPP = payload.customer_id;

      // Check if the customer already exists in the array
      const existingCustomerIndex = state.customers.findIndex(
        c => c.customer_id === customer_idPP
      );

      const newObj = { visited_page: payload.page, title: payload.title };

      if (existingCustomerIndex !== -1) {
        // Customer exists — refresh status/page AND geo/meta (the live payload
        // carries up-to-date region/city/country which a historical entry from
        // /recent-customers may be missing). Only overwrite when the incoming
        // value is present, so we never blank out existing data.
        const ex = state.customers[existingCustomerIndex];
        const keep = (next: any, cur: any) =>
          next !== undefined && next !== null && next !== '' ? next : cur;
        const updatedCustomers = [...state.customers];
        updatedCustomers[existingCustomerIndex] = {
          ...ex,
          active: true,
          first_seen: ex.first_seen || new Date().toISOString(),
          lat: keep(payload.lat, ex.lat),
          lng: keep(payload.lng, ex.lng),
          page: keep(payload.page, ex.page),
          title: keep(payload.title, ex.title),
          region: keep(payload.region, ex.region),
          city: keep(payload.city, ex.city),
          country: keep(payload.country, ex.country),
          country_name: keep(payload.country_name, ex.country_name),
          browser: keep(payload.browser, ex.browser),
          os: keep(payload.os, ex.os),
          device: keep(payload.device, ex.device),
          ip: keep(payload.ip, ex.ip),
          org: keep(payload.org, ex.org),
          refer: keep(payload.refer, ex.refer),
          // history can be null (server sends null when there are no visits, and
          // a conversation opened for an offline visitor seeds it null) — guard
          // the spread so it never throws "history is not iterable".
          history: [...(Array.isArray(ex.history) ? ex.history : []), newObj]
        };

        return {
          ...state,
          customers: updatedCustomers
        };
      } else {
        // Customer doesn't exist, add them to the array
        const newCustomer = {
          browser: payload.browser,
          ip: payload.ip,
          os: payload.os,
          page: payload.page || 'n/a',
          country: payload.country,
          name: payload.name,
          email: payload.email,
          customer_id: customer_idPP,
          refer: payload.refer,
          region: payload.region,
          org: payload.org,
          title: payload.title,
          device: payload.device || "n/a",
          messages: [],
          city: payload.city,
          history: [newObj],
          country_name: payload.country_name,
          active: true,
          browser_count: 1,
          chat_started: payload.chat_started || false,
          customer_typing_text: false,
          customer_typed_text: '',
          latest_active: new Date(),
          first_seen: new Date().toISOString(),
          lat: payload.lat,
          lng: payload.lng
        };

        return {
          ...state,
          customers: [...state.customers, newCustomer]
        };
      }
    },

    removeCustomer: (state, action: PayloadAction<any>) => {
      const updatedCustomers = state.customers.map(customer => {
        if (customer.customer_id === action.payload.customer_id) {
          const newBrowserCount = (customer.browser_count || 1) - 1;

          if (newBrowserCount === 0) {
            // Customer is no longer active
            return { ...customer, active: false, browser_count: 0 };
          } else {
            // Decrement browser_count but keep the customer active
            return {
              ...customer,
              active: true,
              browser_count: newBrowserCount
            };
          }
        }
        return customer;
      });

      state.customers = updatedCustomers;
    },

    customerTypingText: (
      state,
      action: PayloadAction<{ customer_id: string; text: string }>
    ) => {
      return {
        ...state,
        customers: state.customers.map(item => {
          if (item.customer_id === action.payload.customer_id) {
            return {
              ...item,
              customer_typing_text: true,
              customer_typed_text: action.payload.text
            };
          }
          return item;
        })
      };
    },

    receivedStartChatFromCustomer: (
      state,
      action: PayloadAction<{
        customer_id: string;
        email: string;
        name: string;
      }>
    ) => {
      return {
        ...state,
        customers: state.customers.map(item => {
          if (item.customer_id === action.payload.customer_id) {
            return {
              ...item,
              chat_started: true,
              email: action.payload.email,
              name: action.payload.name
            };
          }
          return item;
        })
      };
    },

    customerEndedTheChat: (
      state,
      action: PayloadAction<{ customer_id: string }>
    ) => {
      const { customer_id } = action.payload;

      state.customers = state.customers.map(item => {
        if (item.customer_id === customer_id) {
          const newMessage: Message = {
            id: Date.now().toString(), // Ensure that you generate a unique ID or use your preferred ID method
            content: 'Customer ended the chat',
            type: 'customer',
            agentRead: false,
            timestamp: new Date().toISOString() // Ensure this matches the type defined in your `Message` interface
          };

          return {
            ...item,
            chat_started: false,
            typing_text: '',
            active: true,
            messages: [...(item.messages || []), newMessage],
            customer_typing_text: false,
            customer_typed_text: ''
          };
        }
        return item;
      });
    },

    sendTypingMessage: (
      state,
      action: PayloadAction<{ customerId: string; ownerToken: string }>
    ) => {
      // do nothing here, we just need to informthe middleware.
    },

    receivedMessageFromCustomer: (
      state,
      action: PayloadAction<{ customer_id: string; message: string; attachment?: any }>
    ) => {
      const { customer_id, message, attachment } = action.payload;

      state.customers = state.customers.map(item => {
        if (item.customer_id === customer_id) {
          const newMessage: Message = {
            id: Date.now().toString(), // Ensure that you generate a unique ID or use your preferred ID method
            content: message,
            type: 'customer',
            agentRead: false,
            timestamp: new Date().toISOString(), // Ensure this matches the type defined in your `Message` interface
            attachment: attachment || null
          };

          return {
            ...item,
            chat_started: true,
            typing_text: '',
            active: true,
            messages: [...(item.messages || []), newMessage],
            customer_typing_text: false,
            customer_typed_text: ''
          };
        }
        return item;
      });
    },

    // Replaces a customer's message list with the full history fetched from the
    // API. The live store only holds messages received during this session, so
    // opening a conversation needs this to show past messages. If the visitor
    // isn't in the live list (offline / fresh page load), a minimal entry is
    // created so the conversation can still render.
    setCustomerHistory: (
      state,
      action: PayloadAction<{ customer_id: string; messages: Message[] }>
    ) => {
      const { customer_id, messages } = action.payload;
      const idx = state.customers.findIndex(c => c.customer_id === customer_id);
      if (idx >= 0) {
        state.customers[idx] = { ...state.customers[idx], messages };
      } else {
        state.customers.push({
          customer_id,
          name: '',
          email: '',
          messages,
          customer_typing_text: false,
          customer_typed_text: '',
          latest_active: new Date(),
          chat_started: messages.length > 0,
          history: null
        } as Customer);
      }
    },

    // The agent is viewing a conversation — the middleware turns this into a
    // WS `agent_read` so the visitor's widget shows their messages as read.
    sendAgentRead: (_state, _action: PayloadAction<{ customerId: string }>) => {
      // no-op in the store; handled by webSocketMiddleware
    },

    // The customer read the agent's messages — flip the agent's own bubbles to ✓✓.
    markAgentMessagesRead: (state, action: PayloadAction<{ customer_id: string }>) => {
      const c = state.customers.find(x => x.customer_id === action.payload.customer_id);
      if (c) {
        c.messages = (c.messages || []).map(m =>
          m.type === 'agent' ? { ...m, read: true } : m
        );
      }
    },

    // Merge recent (historical) visitors fetched on app open. Only adds those
    // not already present and marks them inactive — live WS events later upgrade
    // them to active. Never clobbers a live entry.
    mergeRecentCustomers: (state, action: PayloadAction<any[]>) => {
      const existing = new Set(state.customers.map(c => c.customer_id));
      (action.payload || []).forEach((item: any) => {
        if (!item || !item.customer_id || existing.has(item.customer_id)) return;
        existing.add(item.customer_id);
        state.customers.push({
          customer_id: item.customer_id,
          name: item.name || '',
          email: item.email || '',
          browser: item.browser,
          ip: item.ip,
          os: item.os,
          page: item.page || 'n/a',
          country: item.country,
          region: item.region,
          org: item.org,
          device: item.device,
          refer: item.refer,
          title: item.title,
          city: item.city,
          country_name: item.country_name,
          history: item.history ?? null,
          messages: [],
          active: false,
          browser_count: 1,
          chat_started: !!item.chat_started,
          customer_typing_text: false,
          customer_typed_text: '',
          // Use the visitor's real last-seen time so "Latest Active" reflects
          // when they were actually here, not when the app loaded.
          latest_active: item.last_seen ? new Date(item.last_seen) : new Date(),
          blocked: !!item.blocked
        } as Customer);
      });
    },

    // Reflect a block/unblock locally so the UI updates immediately.
    setCustomerBlocked: (
      state,
      action: PayloadAction<{ customer_id: string; blocked: boolean }>
    ) => {
      const c = state.customers.find(
        x => x.customer_id === action.payload.customer_id
      );
      if (c) c.blocked = action.payload.blocked;
    }
  }
});

export const {
  upsertCustomer,
  removeCustomer,
  receivedMessageFromCustomer,
  sendTypingMessage,
  startChat,
  endChat,
  sendMessage,
  addReceivedMessageFromAnotherLocation,
  customerTypingText,
  addMessageToCustomer,
  endChatbyAgent,
  customerEndedTheChat,
  receivedStartChatFromCustomer,
  bulkUpdateCustomers,
  updateCustomer,
  setCustomerHistory,
  mergeRecentCustomers,
  sendAgentRead,
  markAgentMessagesRead,
  setCustomerBlocked
} = trafficSlice.actions;
export default trafficSlice.reducer;
